import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Tag, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Payment dialog — Revolut only.
 * Supports coupon codes (validated before checkout).
 *
 * props:
 *   open, onOpenChange
 *   mode: "subscription" | "ppv" | "live_seminar_ppv"
 *   planId (subscription) / videoId (ppv) / liveSeminarId (live_seminar_ppv)
 *   amountLabel: string preview
 *   basePrice: number (for coupon discount preview)
 */
const PaymentProviderDialog = ({ open, onOpenChange, mode, planId, videoId, liveSeminarId, amountLabel, basePrice }) => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [revolutEnabled, setRevolutEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponCheck, setCouponCheck] = useState(null); // {valid, discount_amount, final_amount} | null
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCouponCode('');
    setCouponCheck(null);
    axios
      .get(`${API}/payments/revolut/config`)
      .then((res) => setRevolutEnabled(!!res.data?.enabled))
      .catch(() => setRevolutEnabled(false));
  }, [open]);

  const checkCoupon = async () => {
    if (!couponCode) return;
    setValidating(true);
    try {
      const payload = mode === 'subscription'
        ? { code: couponCode, plan_id: planId }
        : { code: couponCode, amount: basePrice || 9.99 };
      const res = await axios.post(`${API}/coupons/validate`, payload);
      if (res.data?.valid) {
        setCouponCheck(res.data);
        toast.success(i18n.language === 'el' ? 'Κουπόνι εφαρμόστηκε!' : 'Coupon applied!');
      } else {
        setCouponCheck(null);
        toast.error(res.data?.error || (i18n.language === 'el' ? 'Μη έγκυρο κουπόνι' : 'Invalid coupon'));
      }
    } catch (e) {
      setCouponCheck(null);
      toast.error(e.response?.data?.detail || (i18n.language === 'el' ? 'Μη έγκυρο κουπόνι' : 'Invalid coupon'));
    } finally {
      setValidating(false);
    }
  };

  const startCheckout = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const origin_url = window.location.origin;
      const url = mode === 'subscription'
        ? `${API}/checkout/revolut/subscription`
        : mode === 'live_seminar_ppv'
          ? `${API}/live-seminars/${liveSeminarId}/purchase`
          : `${API}/checkout/revolut/ppv`;
      const body = mode === 'subscription'
        ? { plan_id: planId, origin_url, coupon_code: couponCheck?.valid ? couponCode : undefined }
        : mode === 'live_seminar_ppv'
          ? { origin_url, coupon_code: couponCheck?.valid ? couponCode : undefined }
          : { video_id: videoId, origin_url, coupon_code: couponCheck?.valid ? couponCode : undefined };

      const res = await axios.post(url, body, { headers });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(i18n.language === 'el' ? 'Σφάλμα' : 'Error');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (i18n.language === 'el' ? 'Σφάλμα' : 'Error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-misel-card border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-roboto text-xl">
            {i18n.language === 'el' ? 'Ολοκλήρωση πληρωμής' : 'Complete payment'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {amountLabel}
          </DialogDescription>
        </DialogHeader>

        {/* Coupon section */}
        <div className="space-y-2 pt-2">
          <label className="text-xs uppercase text-gray-500 tracking-wider flex items-center gap-2">
            <Tag size={12} />
            {i18n.language === 'el' ? 'Κωδικός έκπτωσης' : 'Discount code'}
          </label>
          <div className="flex gap-2">
            <Input
              data-testid="coupon-input"
              placeholder={i18n.language === 'el' ? 'Π.χ. WELCOME10' : 'e.g. WELCOME10'}
              value={couponCode}
              onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponCheck(null); }}
              className="bg-misel-bg border-white/20 text-white uppercase font-mono"
            />
            <Button
              data-testid="apply-coupon-btn"
              onClick={checkCoupon}
              disabled={!couponCode || validating}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {validating ? <Loader2 className="animate-spin" size={16} /> : (i18n.language === 'el' ? 'Εφαρμογή' : 'Apply')}
            </Button>
          </div>
          {couponCheck?.valid && (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle2 size={14} />
              <span>
                {i18n.language === 'el' ? 'Έκπτωση' : 'Discount'}: -€{couponCheck.discount_amount?.toFixed?.(2)}
                {' → '}
                <strong>€{couponCheck.final_amount?.toFixed?.(2)}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Pay button — Revolut only */}
        <div className="flex flex-col gap-3 pt-4">
          {revolutEnabled ? (
            <Button
              data-testid="pay-with-revolut-btn"
              onClick={startCheckout}
              disabled={loading}
              className="w-full bg-[#0075EB] hover:bg-[#005BB8] text-white justify-center gap-3 h-14 text-base"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm bg-white text-[#0075EB] font-bold text-sm">R</span>
                  <span>
                    {i18n.language === 'el' ? 'Πληρωμή με Revolut' : 'Pay with Revolut'}
                  </span>
                </>
              )}
            </Button>
          ) : (
            <p className="text-sm text-red-400 text-center py-4">
              {i18n.language === 'el'
                ? 'Το σύστημα πληρωμής δεν είναι διαθέσιμο αυτή τη στιγμή. Παρακαλώ δοκιμάστε αργότερα.'
                : 'Payment system is currently unavailable. Please try again later.'}
            </p>
          )}
          <p className="text-xs text-gray-500 text-center">
            {i18n.language === 'el'
              ? 'Ασφαλής πληρωμή · Κάρτα, Revolut Pay, Apple Pay, Google Pay'
              : 'Secure payment · Card, Revolut Pay, Apple Pay, Google Pay'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentProviderDialog;
